import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { createRole, updateRole } from '../../services/roleService';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Input } from '../ui/input';
import { Stepper } from '../ui/stepper';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';

import ApiBuilderWelcome from './ApiBuilderWelcome';
import BulkHttpVerbConfig from './BulkHttpVerbConfig';
import BulkTableSelection from './BulkTableSelection';

const steps = ['Welcome', 'Basic Information', 'Select Endpoints', 'Configure Endpoints'];

const CreateRole = ({ mode = 'create', existingRole = null }) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(mode === 'edit' ? 1 : 0);
  const [role, setRole] = useState({
    name: '',
    description: '',
    serviceId: '',
    permissions: [],
    isActive: true,
    mcpEnabled: false,
    mcpConfig: {
      description: '',
    },
  });
  const [selectedService, setSelectedService] = useState('');
  const [error, setError] = useState('');

  // State for bulk operations
  const [selectedTables, setSelectedTables] = useState([]);
  const [bulkPermissions, setBulkPermissions] = useState([]);

  const form = useForm({
    defaultValues: {
      name: '',
      description: '',
      isActive: true,
      mcpEnabled: false,
      mcpDescription: '',
    },
  });

  useEffect(() => {
    if (existingRole) {
      setRole({
        name: existingRole.name,
        description: existingRole.description,
        serviceId: existingRole.serviceId,
        permissions: existingRole.permissions || [],
        isActive: existingRole.isActive ?? true,
        mcpEnabled: existingRole.mcpEnabled || false,
        mcpConfig: existingRole.mcpConfig || {},
      });
      form.reset({
        name: existingRole.name,
        description: existingRole.description,
        isActive: existingRole.isActive ?? true,
        mcpEnabled: existingRole.mcpEnabled || false,
        mcpDescription: existingRole.mcpConfig?.description || '',
      });
      setSelectedService(existingRole.serviceId);
      setBulkPermissions(existingRole.permissions || []);

      // Convert existing permissions to selectedTables format
      const existingTables = (existingRole.permissions || []).map(perm => {
        const pathParts = perm.objectName.split('/').filter(Boolean);
        const type = pathParts[0]; // 'table', 'view', or 'proc'
        const name = pathParts[1];
        return {
          name,
          type,
          path: perm.objectName,
        };
      });
      setSelectedTables(existingTables);
    }
  }, [existingRole, form]);

  // Navigation handlers
  const handleNext = async () => {
    if (activeStep === 0) {
      setActiveStep(prevStep => prevStep + 1);
      return;
    }

    if (activeStep === 1) {
      const formData = form.getValues();
      if (!formData.name) {
        setError('Please enter a role name');
        return;
      }
      setRole(prev => ({
        ...prev,
        name: formData.name,
        description: formData.description,
        isActive: formData.isActive,
        mcpEnabled: formData.mcpEnabled,
        mcpConfig: {
          description: formData.mcpDescription,
        },
      }));
    }

    if (activeStep === 2) {
      if (selectedTables.length === 0) {
        setError('Please select at least one object');
        return;
      }
    }

    if (activeStep === 3) {
      const hasValidPermissions = bulkPermissions.some(permission =>
        Object.values(permission.actions).some(enabled => enabled)
      );

      if (!hasValidPermissions) {
        setError('Please select at least one HTTP method for the objects');
        return;
      }
    }

    setError('');
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  // Bulk selection callbacks
  const handleTablesSelected = (tables, serviceId) => {
    // BulkTableSelection already maintains all selections (initial + new)
    // so we can just use what it sends
    setSelectedTables(tables);
    setRole(prev => ({ ...prev, serviceId }));
  };

  const handlePermissionsChange = useCallback(permissions => {
    setBulkPermissions(permissions);
  }, []);

  const handleRemoveTable = useCallback(tableName => {
    setSelectedTables(prev => prev.filter(table => table.name !== tableName));
  }, []);

  // Final submission
  const handleSubmit = async () => {
    try {
      const roleData = {
        name: role.name,
        description: role.description,
        serviceId: role.serviceId,
        permissions: bulkPermissions,
        isActive: role.isActive,
        mcpEnabled: role.mcpEnabled,
        mcpConfig: role.mcpConfig,
      };

      if (!roleData.serviceId) {
        setError('Service ID is required');
        return;
      }

      if (!roleData.permissions || roleData.permissions.length === 0) {
        setError('At least one component permission is required');
        return;
      }

      if (mode === 'edit') {
        await updateRole(existingRole._id, roleData);
      } else {
        await createRole(roleData);
      }

      navigate('/roles');
    } catch (err) {
      console.error('Role save error:', err);
      setError(err.response?.data?.message || 'Failed to save role');
    }
  };

  const handleCancel = () => {
    navigate('/roles');
  };

  const renderBasicInfo = () => (
    <div className="space-y-4">
      <Form {...form}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="Enter role name" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Enter role description" rows={3} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-2">
                <FormControl>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <FormLabel className="!mt-0">Active</FormLabel>
              </FormItem>
            )}
          />

          {/* MCP Server Configuration */}
          <div className="mt-6 pt-6 border-t">
            <h3 className="text-lg font-semibold mb-4 text-ocean-800">MCP Server Configuration</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Enable this role as an MCP (Model Context Protocol) server to provide AI development
              tools with access to your database objects.
            </p>

            <FormField
              control={form.control}
              name="mcpEnabled"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2 mb-4">
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormLabel className="!mt-0">Enable as MCP Server</FormLabel>
                </FormItem>
              )}
            />

            {form.watch('mcpEnabled') && (
              <FormField
                control={form.control}
                name="mcpDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>MCP Server Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Describe what this MCP server provides (e.g., 'Access to Template20 customer and order management procedures')"
                        rows={2}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
      </Form>
    </div>
  );

  return (
    <div className="w-full max-w-6xl mx-auto p-6">
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Stepper steps={steps} activeStep={activeStep} className="mb-8" />

      <Card>
        <CardContent className="pt-6">
          {activeStep === 0 && <ApiBuilderWelcome onNext={handleNext} />}
          {activeStep === 1 && renderBasicInfo()}
          {activeStep === 2 && (
            <BulkTableSelection
              onTablesSelected={handleTablesSelected}
              selectedService={selectedService}
              setSelectedService={setSelectedService}
              initialSelectedTables={selectedTables}
            />
          )}
          {activeStep === 3 && (
            <BulkHttpVerbConfig
              selectedTables={selectedTables}
              selectedService={selectedService}
              onPermissionsChange={handlePermissionsChange}
              existingPermissions={role?.permissions || []}
              onRemoveTable={handleRemoveTable}
            />
          )}
        </CardContent>
      </Card>

      {/* Navigation buttons */}
      {activeStep > 0 && (
        <div className="flex flex-col-reverse sm:flex-row justify-end mt-6 gap-2">
          <Button variant="outline" onClick={handleCancel} className="w-full sm:w-auto">
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button variant="outline" onClick={handleBack} className="w-full sm:w-auto">
              Back
            </Button>
          )}
          {activeStep === steps.length - 1 ? (
            <Button onClick={handleSubmit} variant="ocean" className="w-full sm:w-auto">
              {mode === 'edit' ? 'Update' : 'Create'} Role
            </Button>
          ) : (
            <Button onClick={handleNext} variant="ocean" className="w-full sm:w-auto">
              Next
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CreateRole;
